import { SupportPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  useShiftStatusQuery,
  useWorkScheduleQuery,
} from "@redotech/redo-merchant-app-common/setting/use-work-schedule-query";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  Call,
  CallState,
  VoiceDeviceState,
} from "@redotech/redo-support-common/support/voice/voice-types";
import { VoiceProvider } from "@redotech/redo-support-settings-common/voice-settings";
import { createContext, useState } from "react";

export const VoiceDeviceStateContext = createContext<
  | {
      availabilityStatus: VoiceDeviceState;
      setCurrentCall: (call: Call | undefined) => void;
    }
  | undefined
>(undefined);

export function VoiceDeviceStateContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );
  const [currentCall, setCurrentCall] = useState<Call | undefined>(undefined);

  const voiceProvider =
    team.settings.support?.voice?.provider ?? VoiceProvider.TELNYX;
  const isTelnyxVoiceEnabled =
    voiceProvider === VoiceProvider.TELNYX &&
    !!team.settings.support?.voice?.enabled &&
    hasSupportPermission;

  const shiftStatusQuery = useShiftStatusQuery({
    enabled: isTelnyxVoiceEnabled,
  });
  const scheduleQuery = useWorkScheduleQuery({ enabled: isTelnyxVoiceEnabled });

  const assignableChannels = scheduleQuery.data?.schedule.assignableChannels;
  // Null means every channel, matching the picker's "all channels" state.
  const takesVoiceChannel =
    assignableChannels == null || assignableChannels.includes("voice");

  // Unknown reads as unavailable so a call is never routed to someone we
  // have not yet confirmed is on shift.
  const scheduleDerivedStatus =
    shiftStatusQuery.data?.active && takesVoiceChannel
      ? VoiceDeviceState.AVAILABLE
      : VoiceDeviceState.UNAVAILABLE;

  const callDerivedStatus =
    currentCall && currentCall.state !== CallState.HANGUP
      ? VoiceDeviceState.IN_CALL
      : undefined;

  const availabilityStatus = callDerivedStatus || scheduleDerivedStatus;

  return (
    <VoiceDeviceStateContext.Provider
      value={{ availabilityStatus, setCurrentCall }}
    >
      {children}
    </VoiceDeviceStateContext.Provider>
  );
}
